import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  tenantId: string;
  /** The staff member behind this login, when there is one. Gates the self-service portal. */
  employeeId?: string | null;
  branchId?: string | null;
  roles: string[];
  permissions: string[];
  themeId: string;
  allowThemeOverride: boolean;
  /** The password was issued by an administrator; nothing else opens until it is replaced. */
  mustChangePassword?: boolean;
}

export interface AuthResult {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  activeBranchId: string | null;

  /** Set when a token expired mid-session, so the login page can say so rather than looking blank. */
  sessionExpired: boolean;

  setSession: (result: AuthResult) => void;
  setActiveBranch: (branchId: string | null) => void;
  setSessionExpired: (expired: boolean) => void;
  clear: () => void;

  isAuthenticated: () => boolean;
  has: (permission: string) => boolean;
  hasAny: (...permissions: string[]) => boolean;
}

/**
 * The session.
 *
 * <p><b>Permissions come from the token, not from a separate fetch.</b> The API flattens them into
 * the JWT and re-checks every one server-side, so what is held here only decides what the UI offers
 * — a stale list can hide a button, never grant access.</p>
 *
 * <p>Persisted to localStorage so a reload does not sign the user out. That is the ordinary
 * trade-off for a browser SPA: it survives F5 and it is readable by anything running on the origin,
 * which is why the access token is short-lived (15 minutes) and the refresh token rotates on use.</p>
 */
export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      activeBranchId: null,
      sessionExpired: false,

      setSession: (result) =>
        set({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          user: result.user,
          // Only adopt the server's branch when the user has not chosen one; a refresh must not
          // silently move somebody back to their default branch mid-shift.
          activeBranchId: get().activeBranchId ?? result.user.branchId ?? null,
          sessionExpired: false,
        }),

      setActiveBranch: (activeBranchId) => set({ activeBranchId }),
      setSessionExpired: (sessionExpired) => set({ sessionExpired }),

      clear: () =>
        set({ accessToken: null, refreshToken: null, user: null, activeBranchId: null }),

      isAuthenticated: () => Boolean(get().accessToken),
      has: (permission) => get().user?.permissions.includes(permission) ?? false,
      hasAny: (...permissions) => {
        const held = get().user?.permissions;
        return held ? permissions.some((p) => held.includes(p)) : false;
      },
    }),
    { name: "hms-auth" },
  ),
);
